import { useState } from 'react';

export type OutOfDatePublicationVariant = 'regenerate' | 'update';

export interface UseOutOfDatePublicationVariantArgs {
  /**
   * Whether the queries `isItemsOutOfDate`/`isPublicationOutOfDate` come
   * from are still settling. The hook waits for this to go `false` before
   * deciding a variant at all, so it lands on the right one from the start
   * instead of picking an early guess and correcting it later.
   */
  isLoading: boolean;
  /** Generated content (media or add-ons) no longer matches the project's current content. */
  isItemsOutOfDate: boolean;
  /**
   * Live, but a later edit has staled the published version. The caller is
   * expected to keep this `false` while the media itself is still
   * regenerating — regenerating doesn't update the publication on its own
   * (see MED-6969), so nudging to update before the fresh media has even
   * landed would be premature.
   */
  isPublicationOutOfDate: boolean;
}

/**
 * Decides which of the two variants applies — stale media always takes
 * priority, "update" only becomes relevant once the media itself isn't
 * out of date — and freezes the decision the first time `isLoading` goes
 * `false`, so the shown wording doesn't flip under the user while data is
 * still settling. It re-decides whenever the media's out-of-date flag flips
 * either way, so both the "regenerate" -> "update" handoff (fresh media
 * lands) and the "update" -> "regenerate" handoff (a stale first read gets
 * corrected once the real data arrives) still happen, instead of staying
 * stuck on either variant.
 *
 * Shared by `OutOfDatePublicationWidget` and `OutOfDatePublicationCompactButton`
 * so the two can never disagree on which variant to show.
 */
export const useOutOfDatePublicationVariant = ({
  isLoading,
  isItemsOutOfDate,
  isPublicationOutOfDate,
}: UseOutOfDatePublicationVariantArgs):
  | OutOfDatePublicationVariant
  | undefined => {
  const [variant, setVariant] = useState<
    OutOfDatePublicationVariant | undefined
  >(undefined);

  // `variant` already encodes what `isItemsOutOfDate` was last time we
  // decided, so re-derive whenever that flag no longer matches — in either
  // direction — rather than only allowing the 'regenerate' -> 'update' path.
  const canRederive =
    !variant ||
    (variant === 'regenerate' && !isItemsOutOfDate) ||
    (variant === 'update' && isItemsOutOfDate);

  // Guarded so this only fires on the first settled render, or when
  // `isItemsOutOfDate` flips relative to the frozen variant — an
  // `isPublicationOutOfDate`-only change stays frozen either way.
  if (!isLoading && canRederive) {
    let next: OutOfDatePublicationVariant | undefined;
    if (isItemsOutOfDate) {
      next = 'regenerate';
    } else if (isPublicationOutOfDate) {
      next = 'update';
    }
    if (next !== variant) setVariant(next);
  }

  return variant;
};
